const INFO = {
	main: {
		title: "Dhruti's Portfolio",
		name: "Dhruti Vadlamudi",
		email: "dhrutiv@cs.washington.edu",
	},

	socials: {
		github: "https://github.com/dhrutiv1129",
		linkedin: "https://www.linkedin.com/in/dhruti-vadlamudi/",
		medium: "https://medium.com/@dhrutiv_26687",

	},

	homepage: {
		title: "CS student at the University of Washington working across software engineering, ML research, and product.",
		description:
			"I build reliable, user-centered software across full-stack engineering, applied machine learning, and technical product development—turning ambiguous problems into measurable, scalable systems.",
	},

	

	articles: {
		title: "I'm passionate about pushing the boundaries of what's possible and inspiring the next generation of innovators.",
		description:
			"Chronological collection of my long-form thoughts on programming, leadership, product design, and more.",
	},

	workExperience: [
		{
			company: "Kohl's",
			role: "Software Engineering Intern",
			date: "June 2026 – Present",
			description:
				"Building secure, scalable retail transaction services and Loss Prevention integrations.",
			details:
				"Modernize Java and Spring Boot microservices by replacing deprecated dependencies and remediating 30+ vulnerabilities across gateway, OTP, and widget services. Engineer backend data mappings that improve transaction visibility for Loss Prevention workflows and support reliable retail-platform deployments.",
			tools: ["Java", "Spring Boot", "Microservices", "REST APIs", "Application Security"],
		},
		
		{
			company: "Impact Financial Advisors",
			role: "Software Engineering Intern",
			date: "June 2024 – August 2024",
			description:
				"Automating financial reporting through applied AI, Python, and data-engineering workflows.",
			details:
				"Built an LLM-powered Python reporting assistant that reduced financial report-generation time by approximately 30%. Developed automated Python and Excel pipelines across 10+ financial metrics, partnering with five advisors to productionize reusable data cleaning, validation, and report-generation workflows.",
			tools: ["Python", "LLMs", "Excel", "Data Automation", "Data Validation"],
		},
		{
			company: "Seattle-Tacoma International Airport",
			role: "Software Engineering & Cybersecurity Intern",
			date: "June 2023 - August 2023",
			description:
				"Designed AI-assisted security and compliance workflows for airport operations and risk management.",
			details:
				"Developed AI-assisted documentation and regulatory-monitoring workflows that translated FAA, TSA, and OSHA requirements into actionable airport security and risk-management processes. Supported risk assessments, internal audits, and compliance reporting while helping teams strengthen authentication standards, security controls, and operational resilience.",
			tools: ["AI-Assisted Analysis", "Cybersecurity Controls", "Authentication Standards", "Risk Assessment", "Compliance Automation"],
		},
		{
			company: "SEAL Research Laboratory",
			role: "Software Engineering & Product Intern",
			date: "May 2026 – Present",
			description:
				"Shipping full-stack technical-writing workflows and shaping product commercialization strategy.",
			details:
				"Lead market research and go-to-market planning for WEGO, a Microsoft Word ribbon tool for technical writers. Contribute to its C#, React, and Django REST architecture, delivering figure/equation insertion, document-formatting, configurable text-wrapping, and cross-referencing workflows.",
			tools: ["C#", "React", "Django REST", "JavaScript", "Product Strategy"],
		},
	],

	additionalExperience: [
		{
			organization: "SWECC Labs",
			highlight: "Open-source contributor building tools that help UW students navigate course selection.",
		},
		{
			organization: "Product Management Club UW",
			highlight: "Delivered product recommendations for client engagements with Amazon and Microsoft Money, and Turner Construction.",
		},
		{
			organization: "UW Symphony Orchestra",
			highlight: "Performer in a collaborative, high-discipline orchestral program at the University of Washington.",
		},
	],
	additionalExperienceIntro:
		"I extend my technical and product perspective through open source, client-facing product work, and collaborative campus communities.",

	skills: [
		{ category: "Software Engineering", items: ["Java", "Python", "JavaScript", "C#", "React", "Spring Boot", "Django REST", "REST APIs"] },
		{ category: "AI, Data & ML", items: ["LLMs", "PyTorch", "Computer Vision", "NLP", "Data Automation", "Excel Analytics", "Model Evaluation"] },
		{ category: "Product & Systems", items: ["Microservices", "Application Security", "Technical Product Strategy", "Go-to-Market", "Risk Analysis", "Technical Documentation"] },
	],

	awards: [
	{
		title: "2nd Place, Kohl's Think Tank",
		issuer: "Kohl's",
		date: "June 2026",
		description: `Designed "Kohl's Kapsule," an omnichannel concept pairing in-store curated displays with a swipeable, personalized recommendation dashboard to address retail choice paralysis and drive Gen Z engagement.`,
	},
	{
		title: "Scholarship Recipient, NCHS Orchestra Boosters Scholarship",
		issuer: "NCHS Orchestra Boosters",
		date: "June 2024",
		description: "Selected from 50+ graduating seniors for the $500 scholarship supporting continued engagement in university orchestra.",
	},
	{
		title: "Scholarship Recipient, Britt Johnson Memorial Scholarship",
		issuer: "Scholarship Foundation of Northshore",
		date: "May 2024",
		description: "Selected from 2,000+ students for outstanding achievement in STEM.",
	},
	{
		title: "3rd Place, Deloitte x UW BEA M&A Case Competition",
		issuer: "Deloitte",
		date: "May 2026",
		description: `Led a 48-hour case team building a post-merger integration plan via a 5-phase Stage-Gate framework, modeling 15% cost reduction and 10% revenue growth. Invited to Deloitte's networking event.`,
	},
	{
		title: "10th Place, FBLA National Leadership Conference — Social Media Strategies",
		issuer: "Future Business Leaders of America",
		date: "June 2022",
		description: "Built a full social media campaign for a sports nonprofit, covering budget, timeline, branding, partnerships, and mock content.",
	},
	{
		title: "1st Place, FBLA State Business Leadership Conference — Entrepreneurship",
		issuer: "Future Business Leaders of America",
		date: "April 2022",
		description: "Developed a startup plan for a local bakery; won $100 and qualified for nationals.",
	},
	{
		title: "7th Place, FBLA State Business Leadership Conference — Data Analysis",
		issuer: "Future Business Leaders of America",
		date: "April 2022",
		description: "Analyzed international expansion data across multiple markets; qualified for nationals.",
	},
],
	projects: [
		{
			title: "Brain MRI Shortcut-Learning Investigation",
			summary: "Computer vision model evaluation",
			description:
				"Evaluated a ResNet-18 brain-tumor classifier with Grad-CAM explainability, identifying skull-boundary shortcut learning. Designed crop and augmentation interventions that reduced accuracy from 96.1% to 93.5%, revealing spurious visual signals and model-risk tradeoffs.",
			tools: ["Python", "PyTorch", "ResNet-18", "Grad-CAM", "Computer Vision"],
			linkText: "View Research",
			link: "https://github.com/dhrutiv1129/brain-mri-shortcut-learning",
		},

		{
			title: "Parity Aware Super BPE Tokenizer",
			summary: "Multilingual NLP fairness optimization",
			description:
				"Built a fairness-aware multilingual tokenizer that reduced cross-lingual token disparity from 2.27× to 1.02× across 10 languages while achieving approximately 600× faster training—balancing NLP performance, efficiency, and equitable language coverage.",
			tools: ["Python", "NLP", "Tokenization", "Multilingual ML", "Performance Optimization"],
			linkText: "View Research",
			link: "https://github.com/chendurj/pa_superbpe",
		},

		{
			title: "PCA & SVD Visualizer",
			summary: "Interactive linear algebra education",
			description:
				"Developed an interactive browser-based learning tool that visualizes PCA, SVD, explained variance, and low-rank reconstruction in real time. Implemented the mathematical workflows from scratch with Canvas, enabling hands-on exploration of datasets and 3×3 matrices.",
			tools: ["JavaScript", "HTML/CSS", "Canvas API", "PCA", "SVD"],
			linkText: "View Code",
			link: "https://github.com/dhrutiv1129/PCA-SVD-Visualizer",
			demoText: "Try Live Demo",
			demoLink: "https://heartfelt-zabaione-dd5864.netlify.app/",
		},

		{
			title: "American Option Pricer",
			summary: "Quantitative finance application",
			description:
				"Built a Streamlit quantitative-finance application that prices American calls and puts with a Cox-Ross-Rubinstein binomial tree. Integrates market data and volatility estimation to evaluate early-exercise decisions and communicate pricing-model outputs.",
			tools: ["Python", "Streamlit", "Quantitative Finance", "CRR Binomial Tree", "Market Data APIs"],
			linkText: "View Code",
			link: "https://github.com/dhrutiv1129/options-pricing-model",
			demoText: "Try Live Demo",
			demoLink: "https://options-crrmmzwtzusappq8vbcamve.streamlit.app/",
		},

		{
			title: "Bug Meme Roaster",
			summary: "Developer experience extension",
			description:
				"Created a VS Code developer-experience extension that monitors editor diagnostics and translates bug counts into playful, at-a-glance code-health feedback—making debugging status more visible during development.",
			tools: ["VS Code API", "JavaScript", "Developer Tools", "Diagnostics", "UX Design"],
			linkText: "View Code",
			link: "https://github.com/dhrutiv1129/bug-meme-roaster",
		},

		{
			title: "AI Text Reply Bot",
			summary: "Personalized AI workflow automation",
			description:
				"Developing a Python AI assistant that monitors incoming iMessages, drafts context-aware responses with an LLM, and adapts voice and tone to the recipient—an applied workflow-automation project focused on personalized communication.",
			tools: ["Python", "LLMs", "Automation", "Prompt Engineering", "Personalization"],
			linkText: "View Code",
			link: "https://github.com/dhrutiv1129/TextMessenger",
		},
	],
};

export default INFO;
